import { isValid } from 'date-fns';

import { DrawByProductId } from '../../../features/draws/types';
import { DrawStage } from '../../../types/Draw';
import { ReleaseType } from '../../../types/Products';
import { PurchasingState } from '../../../types/Purchasing';
import { toClientUTCDateFormat } from '../PurchaseForm/utils';
import { StickyCtaUrgencyTone } from './StickyBottomCta';

/** As the designs write it: "Closing on 29 October". */
const COMPACT_DATE_FORMAT = 'd MMMM';

/**
 * The three forms the bar speaks in: a draw closing, a timed release ending,
 * and anything still ahead of the viewer starting. Each has a date, a days and
 * a time wording in `common.json` under `countdown.<copy>`.
 */
type CountdownCopy = 'closing' | 'ending' | 'starting';

/**
 * A deadline the viewer is running out of reads as urgent; one they are still
 * waiting on does not. See `StickyCtaUrgencyTone`.
 */
const TONES: Record<CountdownCopy, StickyCtaUrgencyTone> = {
  closing: 'active',
  ending: 'active',
  starting: 'upcoming',
};

export type StickyCtaUrgency = {
  /** `ProductDate` props — the bar renders one with these. */
  dateProps: {
    date: Date;
    keyCountdownDate: string;
    keyCountdownDays: string;
    keyCountdownTime: string;
    uniqueDateFormat: string;
  };
  tone: StickyCtaUrgencyTone;
};

type Args = {
  closingDate?: string;
  draw?: DrawByProductId;
  drawStage: DrawStage;
  launchDate?: string;
  productState: PurchasingState;
  releaseType: ReleaseType;
};

const urgency = (date: Date, copy: CountdownCopy): StickyCtaUrgency | null =>
  isValid(date)
    ? {
        dateProps: {
          date,
          keyCountdownDate: `countdown.${copy}.date`,
          keyCountdownDays: `countdown.${copy}.days`,
          keyCountdownTime: `countdown.${copy}.time`,
          uniqueDateFormat: COMPACT_DATE_FORMAT,
        },
        tone: TONES[copy],
      }
    : null;

/**
 * The deadline the bottom sticky CTA puts beside its button, or null when the
 * product has none to show (PTE-4262).
 *
 * Every case mirrors the date the transactional block already heads itself
 * with, so scrolling past the block never changes the deadline on screen —
 * only its wording, which the bar keeps to one line.
 */
export const getStickyCtaUrgency = ({
  closingDate,
  draw,
  drawStage,
  launchDate,
  productState,
  releaseType,
}: Args): StickyCtaUrgency | null => {
  const launch = launchDate
    ? new Date(toClientUTCDateFormat(launchDate))
    : undefined;

  if (draw && (releaseType === 'draw' || releaseType === 'auction')) {
    switch (drawStage) {
      case 'UPCOMING':
        return urgency(new Date(draw.openAt), 'starting');
      case 'DRAW':
      case 'DRAW_PRE_ORDER_CLOSED':
        return urgency(new Date(draw.closeAt), 'closing');
      // Pre-order stages count down to the public release instead, falling
      // back to the draw's own deadline when no launch date is set.
      case 'DRAW_PRE_ORDER_OPEN':
      case 'FCFS_PRE_ORDER_OPEN':
      case 'FCFS_PRE_ORDER_CLOSED':
        return launch
          ? urgency(launch, 'starting')
          : urgency(new Date(draw.closeAt), 'closing');
      default:
        return null;
    }
  }

  if (productState === 'AVAILABLE' && releaseType === 'timed' && closingDate) {
    return urgency(new Date(toClientUTCDateFormat(closingDate)), 'ending');
  }

  if (productState === 'COMING_SOON' && launch) {
    return urgency(launch, 'starting');
  }

  return null;
};
